import React, { useState, useEffect } from 'react';
import { Smartphone, Signal, MapPin, Radio, Cpu, Satellite, Zap } from 'lucide-react';

const HowItWorks: React.FC = () => {
  const [simulationState, setSimulationState] = useState<'idle' | 'analyzing' | 'calibrating' | 'locking' | 'stabilized'>('idle');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (simulationState === 'idle') {
        setProgress(0);
        return;
    }

    if (simulationState === 'analyzing') {
        const interval = setInterval(() => {
            setProgress(p => {
                if (p >= 25) {
                    clearInterval(interval);
                    setSimulationState('calibrating');
                    return 25;
                }
                return p + 1;
            });
        }, 50);
        return () => clearInterval(interval);
    }

    if (simulationState === 'calibrating') {
         const interval = setInterval(() => {
            setProgress(p => {
                if (p >= 60) {
                    clearInterval(interval);
                    setSimulationState('locking');
                    return 60;
                }
                return p + 1;
            });
        }, 50);
        return () => clearInterval(interval);
    }

    if (simulationState === 'locking') {
         const interval = setInterval(() => {
            setProgress(p => {
                if (p >= 100) {
                    clearInterval(interval);
                    setSimulationState('stabilized');
                    return 100;
                }
                return p + 2;
            });
        }, 40);
        return () => clearInterval(interval);
    }
    
    if (simulationState === 'stabilized') {
        const timer = setTimeout(() => setSimulationState('idle'), 5000);
        return () => clearTimeout(timer);
    }

  }, [simulationState]);

  const runSimulation = () => {
    if (simulationState !== 'idle') return;
    setSimulationState('analyzing');
  };

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Mekanisme Kalibrasi XCape</h1>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Kami tidak mengubah sistem operasi Anda. XCape bekerja sebagai <strong>Penguat Sinyal Virtual</strong> yang menyelaraskan sensor GPS hardware dengan titik referensi manual yang Anda tentukan.
          </p>
        </div>

        {/* Interactive Dashboard Demo */}
        <div className="max-w-5xl mx-auto mb-24">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
             
             {/* Card Header */}
             <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-6">
                <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Satellite className="text-blue-600 h-6 w-6" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">GPS Signal Optimizer</h3>
                        <p className="text-xs text-slate-500">Hardware Level Calibration Tool</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${simulationState !== 'idle' ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'}`}></span>
                    <span className="text-xs font-mono text-slate-400 uppercase">{simulationState === 'idle' ? 'STANDBY' : 'PROCESSING'}</span>
                </div>
             </div>

            {/* Main Interactive Stage */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Status Column */}
                <div className="space-y-4">
                    <div className={`p-4 rounded-xl border transition-all duration-300 ${['analyzing', 'calibrating', 'locking', 'stabilized'].includes(simulationState) ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                        <div className="flex items-center gap-3">
                            <Signal className="h-5 w-5" />
                            <span className="font-semibold text-sm">Signal Analysis</span>
                        </div>
                    </div>
                    <div className={`p-4 rounded-xl border transition-all duration-300 ${['calibrating', 'locking', 'stabilized'].includes(simulationState) ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                        <div className="flex items-center gap-3">
                            <Radio className="h-5 w-5" />
                            <span className="font-semibold text-sm">Noise Filtering</span>
                        </div>
                    </div>
                    <div className={`p-4 rounded-xl border transition-all duration-300 ${['locking', 'stabilized'].includes(simulationState) ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                        <div className="flex items-center gap-3">
                            <MapPin className="h-5 w-5" />
                            <span className="font-semibold text-sm">Coordinate Lock</span>
                        </div>
                    </div>
                </div>

                {/* Visualizer Column (Center) */}
                <div className="md:col-span-2 bg-slate-900 rounded-2xl p-6 border border-slate-800 flex flex-col justify-between relative shadow-inner">
                    
                    {/* Progress Bar Section */}
                    <div className="mb-8">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">Signal Stability</span>
                            <span className="text-blue-400 font-mono">{progress}%</span>
                        </div>
                        <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Dynamic Graphic */}
                    <div className="flex-grow flex items-center justify-center relative h-40">
                        {simulationState === 'idle' && (
                            <button 
                                onClick={runSimulation}
                                className="bg-brand-primary text-white px-6 py-3 rounded-full font-bold shadow-lg shadow-orange-500/20 hover:bg-orange-600 transition-all flex items-center gap-2 transform hover:scale-105"
                            >
                                <Zap className="h-5 w-5" />
                                Mulai Kalibrasi
                            </button>
                        )}

                        {simulationState !== 'idle' && simulationState !== 'stabilized' && (
                            <div className="relative">
                                {/* Ripple Effect representing Signal */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-32 h-32 border border-blue-500/30 rounded-full animate-ping"></div>
                                    <div className="w-24 h-24 border border-blue-500/50 rounded-full animate-ping animation-delay-200"></div>
                                </div>
                                <div className="w-20 h-20 bg-slate-800 rounded-full border-2 border-blue-500 flex items-center justify-center relative z-10">
                                    <Satellite className="text-blue-400 h-10 w-10 animate-pulse" />
                                </div>
                            </div>
                        )}

                        {simulationState === 'stabilized' && (
                            <div className="text-center animate-fade-in-up">
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-green-500">
                                    <Signal className="h-8 w-8 text-green-500" />
                                </div>
                                <h4 className="text-white font-bold">Signal Optimized</h4>
                                <p className="text-green-400 text-xs">Accuracy: 99.9% (High Precision)</p>
                            </div>
                        )}
                    </div>
                    
                    {/* Console Log */}
                    <div className="mt-6 pt-4 border-t border-slate-800 font-mono text-xs text-slate-500 h-20 overflow-hidden">
                        {simulationState === 'idle' && <p>&gt; Waiting for user input...</p>}
                        {['analyzing', 'calibrating', 'locking', 'stabilized'].includes(simulationState) && <p className="text-blue-400">&gt; Detecting hardware drift...</p>}
                        {['calibrating', 'locking', 'stabilized'].includes(simulationState) && <p className="text-cyan-400">&gt; Filtering atmospheric interference...</p>}
                        {['locking', 'stabilized'].includes(simulationState) && <p className="text-brand-primary">&gt; Locking frequency to manual coordinates...</p>}
                        {['stabilized'].includes(simulationState) && <p className="text-green-400">&gt; STABLE: Hardware output normalized.</p>}
                    </div>
                </div>
            </div>
          </div>
        </div>

        {/* Technical Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Teknologi Kontrol Hardware</h2>
            <p className="text-slate-500 mb-8">
                Saat Anda berada di gedung tinggi atau basement, sinyal GPS menjadi "kotor" dan melenceng. XCape membersihkan sinyal tersebut.
            </p>
            <ul className="space-y-6">
              {[
                { 
                  title: "Manual Reference Point", 
                  desc: "Anda menentukan titik lokasi sebenarnya di peta. XCape menggunakan koordinat ini sebagai 'Ground Truth' untuk driver GPS." 
                },
                { 
                  title: "Signal Noise Cancellation", 
                  desc: "Mengoabaikan data satelit yang lemah atau memantul (multipath error) dan mempertahankan sinyal yang sesuai dengan titik referensi Anda." 
                },
                { 
                  title: "Environmental Normalization", 
                  desc: "Menyesuaikan data barometer dan magnetometer agar sesuai dengan profil lokasi yang dipilih, mencegah anomali sensor." 
                },
                { 
                  title: "Precision Lock", 
                  desc: "Mengunci output koordinat agar tidak terjadi 'jumping' atau pergeseran posisi tiba-tiba saat absensi." 
                }
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mt-1 text-blue-600 font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-slate-800">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          
           {/* Decorative Code Block */}
           <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-slate-900 rounded-2xl p-6 ring-1 ring-slate-800 leading-none flex items-top justify-start space-x-6 shadow-2xl">
                <div className="space-y-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="text-slate-400 font-mono text-xs md:text-sm">
                    <p className="text-slate-500">// GPS Driver Optimization Routine</p>
                    <p className="text-purple-400">const</p> <p className="text-white inline">calibrateSensor</p> = <p className="text-blue-400 inline">async</p> (targetCoords) =&gt; {'{'}<br/>
                    &nbsp;&nbsp;<p className="text-slate-500">// Filter noisy signals</p><br/>
                    &nbsp;&nbsp;<p className="text-purple-400 inline">const</p> cleanSignal = <p className="text-yellow-400 inline">SignalFilter</p>.removeNoise(hardwareInput);<br/>
                    &nbsp;&nbsp;<br/>
                    &nbsp;&nbsp;<p className="text-slate-500">// Align with user input</p><br/>
                    &nbsp;&nbsp;<p className="text-purple-400 inline">if</p> (cleanSignal.drift &gt; 5.0) {'{'}<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;return <p className="text-green-400 inline">HardwareBridge</p>.forceLock(targetCoords);<br/>
                    &nbsp;&nbsp;{'}'}<br/>
                    &nbsp;&nbsp;<p className="text-purple-400 inline">return</p> cleanSignal;<br/>
                    {'}'}
                </div>
            </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;